import { useEffect } from "react";
import websocketService from "services/websocket";
import type { ServerBasePrice } from "services/types";
import type { CandleWithFlag } from "../model/types";
import { candleBucket, nextBucket, shouldOpenFallback } from "./candleBucket";
import { createLiveCandle } from "./createLiveCandle";

const CHECK_INTERVAL_MS = 200;
const GRACE_MS = Number(process.env.REACT_APP_CANDLE_OPEN_GRACE_MS ?? 2000);

interface UseCandleOpenTickerParams {
  symbol: string;
  chartType: "line" | "candlestick" | "histogram";
  timeframeMs: number;
  dataRef: React.MutableRefObject<(ServerBasePrice | CandleWithFlag)[]>;
  onCandleOpen: () => void;
}

export function useCandleOpenTicker({
  symbol,
  chartType,
  timeframeMs,
  dataRef,
  onCandleOpen,
}: UseCandleOpenTickerParams) {
  useEffect(() => {
    if (chartType === "line") return;

    const id = setInterval(() => {
      const candles = dataRef.current as CandleWithFlag[];
      if (!candles.length) return;

      const lastCandle = candles[candles.length - 1];
      const lastBucket = candleBucket(Date.parse(lastCandle.timestamp), timeframeMs);
      const nextBucketStart = nextBucket(lastBucket, timeframeMs);
      const publisherTs = websocketService.getLastPublisherTsMs(symbol);
      const nowMs =
        publisherTs ?? websocketService.getCurrentServerTime();

      if (
        !shouldOpenFallback(nowMs, nextBucketStart, GRACE_MS, lastBucket)
      ) {
        return;
      }

      candles.push(
        createLiveCandle(
          nextBucketStart,
          lastCandle.close,
          lastCandle.close,
          lastCandle.close,
          lastCandle.close,
        ),
      );
      onCandleOpen();
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(id);
  }, [symbol, chartType, timeframeMs, dataRef, onCandleOpen]);
}

export { GRACE_MS, shouldOpenFallback };
